"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const plattar_api_1 = require("@plattar/plattar-api");
const product_controller_1 = require("./controllers/product-controller");
const viewer_controller_1 = require("./controllers/viewer-controller");
const configurator_controller_1 = require("./controllers/configurator-controller");
const vto_controller_1 = require("./controllers/vto-controller");
/**
 * This tracks the current embed type
 */
var EmbedType;
(function (EmbedType) {
    EmbedType[EmbedType["Viewer"] = 0] = "Viewer";
    EmbedType[EmbedType["Configurator"] = 1] = "Configurator";
    EmbedType[EmbedType["VTO"] = 2] = "VTO";
})(EmbedType || (EmbedType = {}));
/**
 * This is the primary <plattar-embed /> node that allows easy embedding
 * of Plattar related content
 */
class PlattarEmbed extends HTMLElement {
    constructor() {
        super();
        // this is the current embed type, viewer by default
        this._currentType = EmbedType.Viewer;
        this._controller = null;
    }
    get viewer() {
        return this._controller ? this._controller.element : null;
    }
    connectedCallback() {
        const embedType = this.hasAttribute("embed-type") ? this.getAttribute("embed-type") : "viewer";
        if (embedType) {
            switch (embedType.toLowerCase()) {
                case "viewer":
                    this._currentType = EmbedType.Viewer;
                    break;
                case "vto":
                    this._currentType = EmbedType.VTO;
                    break;
                case "configurator":
                    this._currentType = EmbedType.Configurator;
                    break;
                default:
                    this._currentType = EmbedType.Viewer;
            }
        }
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "attributes") {
                    this._OnAttributesUpdated();
                }
            });
        });
        observer.observe(this, {
            attributes: true
        });
        const server = this.hasAttribute("server") ? this.getAttribute("server") : "production";
        plattar_api_1.Server.create(plattar_api_1.Server.match(server || "production"));
        const sceneID = this.hasAttribute("scene-id") ? this.getAttribute("scene-id") : null;
        const productID = this.hasAttribute("product-id") ? this.getAttribute("product-id") : null;
        // decide which controller to initialise
        if (this._currentType === EmbedType.Viewer) {
            // initialise product if scene-id is missing but product-id is defined
            if (!sceneID && productID) {
                this._controller = new product_controller_1.ProductController(this);
            }
            else if (sceneID) {
                this._controller = new viewer_controller_1.ViewerController(this);
            }
        }
        else if (this._currentType === EmbedType.Configurator) {
            this._controller = new configurator_controller_1.ConfiguratorController(this);
        }
        else if (this._currentType === EmbedType.VTO) {
            this._controller = new vto_controller_1.VTOController(this);
        }
        const init = this.hasAttribute("init") ? this.getAttribute("init") : null;
        if (init === "ar") {
            this.startAR();
        }
        else if (init === "viewer") {
            this.startViewer();
        }
        else if (init === "qrcode") {
            this.startQRCode();
        }
        else if (init === "ar-fallback-qrcode") {
            this.startAR().then(() => {
                // nothing to do, launched successfully
            }).catch((_err) => {
                this.startQRCode();
            });
        }
        else if (init === "ar-fallback-viewer") {
            this.startAR().then(() => {
                // nothing to do, launched successfully
            }).catch((_err) => {
                this.startViewer();
            });
        }
    }
    initAR() {
        return new Promise((accept, reject) => {
            if (!this._controller) {
                return reject(new Error("PlattarEmbed.initAR() - cannot execute as controller has not loaded yet"));
            }
            return this._controller.initAR().then(accept).catch(reject);
        });
    }
    startAR() {
        return new Promise((accept, reject) => {
            if (!this._controller) {
                return reject(new Error("PlattarEmbed.startAR() - cannot execute as controller has not loaded yet"));
            }
            return this._controller.startAR().then(accept).catch(reject);
        });
    }
    startViewer() {
        return new Promise((accept, reject) => {
            if (!this._controller) {
                return reject(new Error("PlattarEmbed.startViewer() - cannot execute as controller has not loaded yet"));
            }
            return this._controller.startRenderer().then(accept).catch(reject);
        });
    }
    startQRCode(options = null) {
        return new Promise((accept, reject) => {
            if (!this._controller) {
                return reject(new Error("PlattarEmbed.startQRCode() - cannot execute as controller has not loaded yet"));
            }
            return this._controller.startQRCode(options).then(accept).catch(reject);
        });
    }
    /**
     * This will remove the currently active Renderer
     *
     * @returns - true if removed successfully, false otherwise
     */
    removeRenderer() {
        if (!this._controller) {
            return false;
        }
        return this._controller.removeRenderer();
    }
    /**
     * This is called by the observer if any of the embed attributes have changed
     * based on the state of the embed, we update the internal structure accordingly
     */
    _OnAttributesUpdated() {
        if (this._controller) {
            this._controller.onAttributesUpdated();
        }
    }
}
exports.default = PlattarEmbed;
